"use client";

import { cn } from "@/lib/utils";
import type { PageModule } from "@/types/modules";
import dynamic from "next/dynamic";
import {
  LIVE_PHONE_HEIGHT,
  LIVE_PHONE_WIDTH,
  type LiveSlide,
} from "./live-pages";

const LivePhoneBody = dynamic(
  () => import("./live-phone").then((m) => m.LivePhoneBody),
  { ssr: false },
);

const LiveScreenBody = dynamic(
  () => import("./live-phone").then((m) => m.LiveScreen),
  { ssr: false },
);

const LiveCommentBarBody = dynamic(
  () => import("./live-phone").then((m) => m.LiveCommentBar),
  { ssr: false },
);

const LiveQrCardBody = dynamic(
  () => import("./live-qr").then((m) => m.LiveQrCard),
  {
    ssr: false,
    loading: () => (
      <div className="aspect-[5/6] w-full rounded-xl border border-neutral-200 bg-white" />
    ),
  },
);

const ProductDeviceBody = dynamic(
  () => import("./product-device").then((m) => m.ProductDevice),
  { ssr: false },
);

function phoneSize(scale: number) {
  return { width: LIVE_PHONE_WIDTH * scale, height: LIVE_PHONE_HEIGHT * scale };
}

export function LivePhone({
  slide,
  open,
  dark,
  scale = 0.6,
  className,
}: {
  slide: LiveSlide;
  open?: boolean;
  dark?: boolean;
  scale?: number;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      inert
      className={cn("pointer-events-none relative shrink-0 select-none", className)}
      style={phoneSize(scale)}
    >
      <LivePhoneBody slide={slide} open={open} dark={dark} scale={scale} />
    </div>
  );
}

export function LiveScreenCrop({
  slide,
  open,
  className,
}: {
  slide: LiveSlide;
  open?: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      inert
      className={cn(
        "pointer-events-none relative h-[348px] w-full max-w-[340px] overflow-hidden rounded-t-xl border border-b-0 border-neutral-200 bg-white shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)] select-none",
        className,
      )}
    >
      <div className="absolute inset-x-0 top-0 h-[600px]">
        <LiveScreenBody slide={slide} open={open} />
      </div>
    </div>
  );
}

export function LiveCommentBar({
  slide,
  className,
}: {
  slide: LiveSlide;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      inert
      className={cn(
        "pointer-events-none relative h-[52px] w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 select-none",
        className,
      )}
    >
      <LiveCommentBarBody slide={slide} />
    </div>
  );
}

export function LiveQrCard({ className }: { className?: string }) {
  return (
    <div className={className}>
      <LiveQrCardBody />
    </div>
  );
}

export function LiveDevice({
  pages,
  scale = 0.6,
  autoAdvanceMs,
  className,
}: {
  pages: PageModule[];
  scale?: number;
  autoAdvanceMs?: number;
  className?: string;
}) {
  return (
    <div className={cn("relative shrink-0", className)} style={phoneSize(scale)}>
      <ProductDeviceBody
        pages={pages}
        scale={scale}
        autoAdvanceMs={autoAdvanceMs}
      />
    </div>
  );
}
